import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import Dialog from "../../components/ui/Dialog";
import DataTable, {
  Column,
  DataTableFetchParams,
  DataTableFilterDef,
} from "../../components/ui/DataTable";
import { listingsService } from "../../services/listings.service";
import { FacebookListing } from "../../types/listing";
import { getApiPath } from "../../config/api.config";

const formatPrice = (amount: string | null, currency: string | null): string => {
  if (!amount) return "-";
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return `${currency || ""} ${amount}`.trim();

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch {
    return `${currency || ""} ${numeric.toFixed(2)}`.trim();
  }
};

const toPercent = (value: number): string =>
  `${Math.max(0, Math.min(1, value)) * 100}%`;

const resolveImageUrl = (imageUrl: string): string =>
  imageUrl.startsWith("http") ? imageUrl : getApiPath(imageUrl);

const FacebookListingsPage = () => {
  const { t } = useTranslation("common");
  const [previewItem, setPreviewItem] = useState<FacebookListing | null>(null);

  const handleFetch = useCallback(async (params: DataTableFetchParams) => {
    const res = await listingsService.listFacebook({
      page: params.page,
      take: params.pageSize,
      orderBy: params.sortBy || "createdAt",
      order: params.sortOrder || "DESC",
      search: params.search || undefined,
      sourceId: params.filters?.sourceId || undefined,
    });

    return {
      data: res.data,
      total: res.meta.itemCount,
      totalPages: res.meta.pageCount,
    };
  }, []);

  const columns: Column<FacebookListing>[] = useMemo(
    () => [
      {
        key: "imageUrl",
        header: t("reference_image"),
        className: "w-32",
        render: (row) => (
          <button
            type="button"
            onClick={() => row.imageUrl && setPreviewItem(row)}
            disabled={!row.imageUrl}
            className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 disabled:cursor-default enabled:cursor-zoom-in enabled:hover:ring-2 enabled:hover:ring-blue-400 transition"
            title={row.imageUrl ? t("click_to_enlarge") : t("no_image")}
          >
            {row.imageUrl ? (
              <>
                <img
                  src={resolveImageUrl(row.imageUrl)}
                  alt={row.cardName || row.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                {row.matchRegion ? (
                  <div
                    className="absolute border-2 border-amber-400 bg-amber-300/10 shadow-[0_0_0_9999px_rgba(0,0,0,0.08)] pointer-events-none"
                    style={{
                      left: toPercent(row.matchRegion.x),
                      top: toPercent(row.matchRegion.y),
                      width: toPercent(row.matchRegion.width),
                      height: toPercent(row.matchRegion.height),
                    }}
                  />
                ) : null}
                {row.matchConfidence != null ? (
                  <div className="absolute left-1 bottom-1 px-1.5 py-0.5 rounded bg-black/75 text-white text-[10px] font-medium">
                    {Math.round(row.matchConfidence * 100)}%
                  </div>
                ) : null}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-xs text-gray-400 px-2 text-center">
                {t("no_image")}
              </div>
            )}
          </button>
        ),
      },
      {
        key: "cardName",
        header: t("card_name"),
        sortable: true,
        className: "min-w-[14rem]",
        render: (row) => (
          <div className="space-y-1">
            <div className="font-medium text-gray-900 dark:text-gray-100 line-clamp-2">
              {row.cardName || row.title}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {row.sourceName || row.sourceId}
            </div>
          </div>
        ),
      },
      {
        key: "priceAmount",
        header: t("price"),
        className: "whitespace-nowrap font-semibold",
        render: (row) => formatPrice(row.priceAmount, row.currency),
      },
      {
        key: "originalMessage",
        header: t("original_message"),
        className: "min-w-[24rem] max-w-[36rem]",
        render: (row) => (
          <div className="space-y-1">
            <div className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap line-clamp-4">
              {row.originalMessage || "-"}
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
              <span>
                {t("seller")}: {row.sellerName || "-"}
              </span>
              <span>
                {t("group")}: {row.groupName || "-"}
              </span>
            </div>
          </div>
        ),
      },
      {
        key: "sourceUrl",
        header: t("source_link"),
        className: "min-w-[10rem]",
        render: (row) => (
          <a
            href={row.sourceUrl}
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 hover:underline"
          >
            {t("view_all")}
          </a>
        ),
      },
      {
        key: "createdAt",
        header: t("created_at"),
        sortable: true,
        className: "whitespace-nowrap",
        render: (row) =>
          new Date(row.postedAt || row.createdAt).toLocaleString(),
      },
    ],
    [t],
  );

  const filterDefs: DataTableFilterDef[] = useMemo(
    () => [{ key: "sourceId", label: t("source"), type: "text" }],
    [t],
  );

  return (
    <Layout title={t("facebook_listings")}>
      <div className="space-y-4 h-full flex flex-col">
        <Card className="p-4 shadow-sm flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                {t("facebook_listings")}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t("facebook_listings_hint")}
              </p>
            </div>
          </div>
        </Card>

        <DataTable
          className="flex-1 min-h-0"
          columns={columns}
          onFetch={handleFetch}
          filterDefs={filterDefs}
          initialState={{
            sortBy: "createdAt",
            sortOrder: "DESC",
            pageSize: 25,
          }}
          emptyMessage={t("noData")}
        />
      </div>
      <Dialog
        open={Boolean(previewItem)}
        title={previewItem?.cardName || previewItem?.title || t("preview_image")}
        onClose={() => setPreviewItem(null)}
      >
        {previewItem?.imageUrl ? (
          <div className="space-y-3">
            <div className="relative w-full rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900">
              <img
                src={resolveImageUrl(previewItem.imageUrl)}
                alt={previewItem.cardName || previewItem.title}
                className="w-full h-auto max-h-[70vh] object-contain"
              />
              {previewItem.matchRegion ? (
                <div
                  className="absolute border-4 border-amber-400 bg-amber-300/10 shadow-[0_0_0_9999px_rgba(0,0,0,0.12)] pointer-events-none"
                  style={{
                    left: toPercent(previewItem.matchRegion.x),
                    top: toPercent(previewItem.matchRegion.y),
                    width: toPercent(previewItem.matchRegion.width),
                    height: toPercent(previewItem.matchRegion.height),
                  }}
                />
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
              <span>
                {t("card_name")}: {previewItem.cardName || previewItem.title}
              </span>
              <span>
                {t("price")}:{" "}
                {formatPrice(previewItem.priceAmount, previewItem.currency)}
              </span>
              {previewItem.matchConfidence != null ? (
                <span>
                  {t("confidence")}: {Math.round(previewItem.matchConfidence * 100)}%
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
      </Dialog>
    </Layout>
  );
};

export default FacebookListingsPage;
